import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Executor } from "../../../../../../../services";
import { useExecutorStatus } from "../../../../../../../hooks";
import { CellContentRoot } from "../../../../../../LightTable";
import { getExecutorStatuses } from "../../../../constants";
import { Row } from "..";

const StatusCellContent: React.FC<StatusCellContent.Props> = ({ item }) => {
	const { t } = useTranslation();
	const { getStatus } = useExecutorStatus();
	const EXECUTOR_STATUSES = useMemo(() => getExecutorStatuses(t), [t]);

	const getStatusData = useCallback(
		(rowData) => {
			const isWorking = rowData?.isWorking ?? false;

			const onOwnOrder =
				rowData?.executorToOrders?.find(
					({ order }) =>
						order?.source === "executor" && !order?.closedAt,
				) ?? false;

			const hasDebt = rowData?.debt ?? false;

			let status = rowData?.status;

			if (isWorking) {
				if (hasDebt) {
					if (onOwnOrder) {
						status = "ownOrderDebt";
					} else {
						status = "availableDebt";
					}
				} else if (onOwnOrder) {
					status = "ownOrder";
				}
			} else {
				status = "notWorking";
			}

			const statusData = EXECUTOR_STATUSES[status];

			return statusData;
		},
		[EXECUTOR_STATUSES],
	);

	const getStatusBackgroundColor = useCallback(
		(rowData) => {
			const online = rowData?.online ?? false;

			const statusData = getStatusData(rowData);

			return online ? statusData?.color : statusData?.offlineColor;
		},
		[getStatusData],
	);

	const getStatusFontColor = useCallback(
		(rowData) => {
			const statusData = getStatusData(rowData);

			return statusData?.fontColor;
		},
		[getStatusData],
	);

	return (
		<CellContentRoot
			alignItems="center"
			w="100%"
			h="100%"
			p="0"
			cursor="pointer"
		>
			<Row
				background={getStatusBackgroundColor(item)}
				color={getStatusFontColor(item)}
				active={item.active}
			>
				{<span>{getStatus(item as Executor.Model)}</span>}
			</Row>
		</CellContentRoot>
	);
};

declare namespace StatusCellContent {
	interface Props {
		item: Executor.Model;
	}
}

export default React.memo(StatusCellContent);
